import resolveTokkoBrokerUrl from '@/utils/resolveTokkoBrokerUrl';
import { FILE_PREVIEW_MAX_BYTES } from '@/store/slices/components/fileManager/utils';
import ApiService from './ApiService';
import { createAbortError } from './fileManager/multipartUpload';

class FileManagerService {
  static async getContacts(query) {
    const composedURL = `/api3/contact/?q=${encodeURIComponent(query)}`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getItems({ parentId, orderBy, page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });

    if (parentId != null) {
      params.set('parent_id', String(parentId));
    }

    if (orderBy) {
      params.set('order_by', orderBy);
    }

    const composedURL = `/api3/files/items/?${params.toString()}`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getItem(itemId) {
    const response = await ApiService.get(`/api3/files/items/${itemId}/`);
    return response;
  }

  static async getItemAncestors(itemId) {
    const response = await ApiService.get(
      `/api3/files/items/${itemId}/ancestors/`
    );
    return response;
  }

  static async getEntityFiles(entityType, entityId) {
    const response = await ApiService.get(
      `/api3/files/entities/${entityType}/${entityId}/files/`
    );
    return response;
  }

  static async searchItems({
    q,
    dateFrom,
    dateTo,
    createdBy,
    propertyIds,
    contactIds,
    developmentIds,
    matchMode,
    orderBy,
    page = 1,
    pageSize = 20,
  } = {}) {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });

    if (q) {
      params.set('q', q);
    }

    if (dateFrom) {
      params.set('date_from', dateFrom);
    }

    if (dateTo) {
      params.set('date_to', dateTo);
    }

    if (createdBy) {
      params.set('created_by', createdBy);
    }

    if (propertyIds) {
      params.set('property_ids', propertyIds);
    }

    if (contactIds) {
      params.set('contact_ids', contactIds);
    }

    if (developmentIds) {
      params.set('development_ids', developmentIds);
    }

    if (matchMode) {
      params.set('match_mode', matchMode);
    }

    if (orderBy) {
      params.set('order_by', orderBy);
    }

    const composedURL = `/api3/files/items/search/?${params.toString()}`;
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async createFolder({ name, parentId = null }) {
    const response = await ApiService.post('/api3/files/items/folder/', {
      name,
      parent_id: parentId,
    });
    return response;
  }

  static async updateItem({ itemId, name, description } = {}) {
    const payload = {};

    if (name !== undefined) {
      payload.name = name;
    }

    if (description !== undefined) {
      payload.description = description;
    }

    const response = await ApiService.patch(
      `/api3/files/items/${itemId}/`,
      payload
    );
    return response;
  }

  static async renameItem({ itemId, name }) {
    return FileManagerService.updateItem({ itemId, name });
  }

  static async moveItem({ itemId, newParentId }) {
    const payload = newParentId == null ? {} : { new_parent_id: newParentId };

    const response = await ApiService.post(
      `/api3/files/items/${itemId}/move/`,
      payload
    );
    return response;
  }

  static async bulkDeleteItems(ids) {
    const response = await ApiService.post('/api3/files/items/bulk-delete/', {
      ids,
    });
    return response;
  }

  static async createLink({
    name,
    url,
    parentId = null,
    entities = [],
    duplicateStrategy,
  }) {
    const payload = {
      name,
      url,
      parent_id: parentId,
    };

    if (entities.length > 0) {
      payload.entities = entities;
    }

    if (duplicateStrategy) {
      payload.duplicate_strategy = duplicateStrategy;
    }

    const response = await ApiService.post('/api3/files/items/link/', payload);
    return response;
  }

  static async getEntityLocation(entityType, entityId) {
    const response = await ApiService.get(
      `/api3/files/entities/${String(
        entityType
      ).toLowerCase()}/${entityId}/location/`
    );
    return response;
  }

  static async createEntityLink({
    entityType,
    entityId,
    name,
    url,
    entities = [],
    duplicateStrategy,
  }) {
    const payload = { name, url };

    if (entities.length > 0) {
      payload.entities = entities;
    }

    if (duplicateStrategy) {
      payload.duplicate_strategy = duplicateStrategy;
    }

    const response = await ApiService.post(
      `/api3/files/entities/${String(
        entityType
      ).toLowerCase()}/${entityId}/link/`,
      payload
    );
    return response;
  }

  static async initEntityUpload({
    entityType,
    entityId,
    file,
    entities = [],
    duplicateStrategy,
  }) {
    const payload = {
      name: file.name,
      content_type: file.type || 'application/octet-stream',
      size: file.size,
    };

    if (entities.length > 0) {
      payload.entities = entities;
    }

    if (duplicateStrategy) {
      payload.duplicate_strategy = duplicateStrategy;
    }

    const response = await ApiService.post(
      `/api3/files/entities/${String(
        entityType
      ).toLowerCase()}/${entityId}/upload-init/`,
      payload
    );
    return response;
  }

  static async checkDuplicates({ parentId = null, items }) {
    const response = await ApiService.post(
      '/api3/files/items/duplicate-check/',
      { parent_id: parentId, items }
    );
    return response;
  }

  static async checkEntityDuplicates({ entityType, entityId, items }) {
    const response = await ApiService.post(
      `/api3/files/entities/${String(
        entityType
      ).toLowerCase()}/${entityId}/duplicate-check/`,
      { items }
    );
    return response;
  }

  static async initUpload({ file, parentId = null, duplicateStrategy }) {
    const payload = {
      parent_id: parentId,
      name: file.name,
      content_type: file.type || 'application/octet-stream',
      size: file.size,
    };

    if (duplicateStrategy) {
      payload.duplicate_strategy = duplicateStrategy;
    }

    const response = await ApiService.post(
      '/api3/files/items/upload-init/',
      payload
    );
    return response;
  }

  // The gateway authenticates the PUT with the session cookie (nginx
  // auth_request), so it bypasses ApiService and its JWT/JSON headers; a part
  // must go out with no Content-Type or other headers.
  // XMLHttpRequest is used because fetch cannot report upload progress.
  static putUploadPart(url, body, { signal, onUploadProgress } = {}) {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(createAbortError());
        return;
      }

      const xhr = new XMLHttpRequest();
      const onAbort = () => xhr.abort();
      const stopListening = () => signal?.removeEventListener('abort', onAbort);

      xhr.open('PUT', url);
      xhr.withCredentials = true;
      xhr.upload.onprogress = (event) => {
        if (onUploadProgress) {
          onUploadProgress(event.loaded);
        }
      };
      xhr.onload = () => {
        stopListening();

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ status: xhr.status });
          return;
        }

        if (xhr.status === 401) {
          ApiService.handleUnauthorizedRedirect();
        }

        const error = new Error(
          `Gateway part upload failed with status ${xhr.status}`
        );
        error.status = xhr.status;
        reject(error);
      };
      xhr.onerror = () => {
        stopListening();
        reject(new Error('Gateway part upload failed: network error'));
      };
      xhr.onabort = () => {
        stopListening();
        reject(createAbortError());
      };

      signal?.addEventListener('abort', onAbort, { once: true });
      xhr.send(body);
    });
  }

  // The gateway authenticates the GET with the session cookie, so it bypasses
  // ApiService and its JWT/JSON headers; sending none avoids a CORS preflight.
  static async getFilePreviewBlob(
    fileUrl,
    { signal, mimeType, maxBytes = FILE_PREVIEW_MAX_BYTES } = {}
  ) {
    const response = await fetch(resolveTokkoBrokerUrl(fileUrl), {
      credentials: 'include',
      signal,
    });

    if (!response.ok) {
      if (response.status === 401) {
        ApiService.handleUnauthorizedRedirect();
      }

      const error = new Error(
        `Gateway file preview failed with status ${response.status}`
      );
      error.status = response.status;
      error.code = 'failed';
      throw error;
    }

    const tooLargeError = () => {
      const error = new Error('File is too large to preview');
      error.code = 'too_large';
      return error;
    };

    if (Number(response.headers.get('Content-Length')) > maxBytes) {
      throw tooLargeError();
    }

    const blob = await response.blob();

    if (blob.size > maxBytes) {
      throw tooLargeError();
    }

    return new Blob([blob], { type: mimeType });
  }

  static async abortUpload(itemId) {
    const response = await ApiService.post(
      `/api3/files/items/${itemId}/upload-abort/`
    );
    return response;
  }

  static async deleteItem(itemId) {
    const response = await ApiService.delete(`/api3/files/items/${itemId}/`);
    return response;
  }

  static async completeUpload(itemId) {
    const response = await ApiService.post(
      `/api3/files/items/${itemId}/upload-complete/`
    );
    return response;
  }

  static async linkItemEntity(
    itemId,
    { entityType, entityId, isPublic, role }
  ) {
    const payload = {
      entity_type: entityType,
      entity_id: entityId,
    };

    if (role != null) {
      payload.role = role;
    }

    if (entityType !== 'CONTACT' && isPublic != null) {
      payload.is_public = Boolean(isPublic);
    }

    const response = await ApiService.post(
      `/api3/files/items/${itemId}/link-entity/`,
      payload
    );
    return response;
  }

  static async unlinkItemEntity(itemId, { entityType, entityId }) {
    const response = await ApiService.post(
      `/api3/files/items/${itemId}/unlink-entity/`,
      {
        entity_type: entityType,
        entity_id: entityId,
      }
    );
    return response;
  }

  static async updateViewPreference(fileManagerView, { signal } = {}) {
    const response = await ApiService.patch(
      '/api3/files/preferences/view/',
      {
        file_manager_view: fileManagerView,
      },
      undefined,
      false,
      { signal }
    );
    return response;
  }
}

export default FileManagerService;
